import axios from "axios";
import React from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useLoadingState } from "../Context";
import { Box } from "@mui/material";
import OrderNotFoundScreen from "./OrderNotFoundScreen";
import SearchBar from "./SearchBar";
import TemplateRenderer from "./templates/TemplateRenderer";
import { useAppContext } from "../AppContext";

/**
 * DIY tracking page for CONFIG-DRIVEN brands (brand.source === "api") — the template-path twin of
 * the legacy Order.js, which stays untouched and keeps serving every fallback brand.
 *
 * The fetch/lookup logic below deliberately MIRRORS Order.js line-for-line (same lambda, same
 * request shapes, same failure handling) — the only differences are that merchantId comes from the
 * resolved brand config instead of the hardcoded domainDetails map, and the result renders through
 * TemplateRenderer (Classic / Modern / Minimal), each a self-contained page with the full
 * functionality of the legacy one (search, not-found, OTP gate, NDR actions, track-another).
 */
export default function TemplateOrder() {
    const [searchParams] = useSearchParams()
    const { id } = useParams()
    const { isLoading, setIsLoading } = useLoadingState()
    const [data, setData] = React.useState([])
    const [found, setFound] = React.useState(true)

    const { setValues, brand } = useAppContext();

    const merchantId = brand?.id;

    // Staging/dev builds only (flag absent from .env.prod): the ONLY track_waybill_p deployment is
    // PROD, while staging configs carry STAGING merchant oids — scoped lookups can never match, so
    // every AWB would come back "not found". Unscoped lookups are not a new exposure: the public
    // t.proship.in page has always queried without a merchantId.
    const SKIP_MERCHANT_SCOPE = process.env.REACT_APP_SKIP_MERCHANT_SCOPE === 'true';

    let passedQasQueryParam = searchParams.get('q');
    let searchBy = searchParams.get('searchBy');
    let value = searchParams.get('ref_awb_number');
    let awbSearch = searchParams.get('awbNo');

    if (passedQasQueryParam) {
        searchBy = 'waybill';
        value = passedQasQueryParam;
    }

    const fetchData = (request) => {
        setIsLoading(true);

        let req = axios.post(`https://r1fiocxmcf.execute-api.ap-south-1.amazonaws.com/proship/track_waybill_p`, {
            queryStringParameters: request
        });

        req.then((response) => {

            const orderDetails = response.data?.message?.waybillDetails?.pop();
            let orderFound = true;

            if ((orderDetails.failedReason && ['wrong awb or awb doesn\'t exist', 'wrong shipping or shipping doesn\'t exist'].includes(orderDetails.failedReason))) {
                orderFound = false;
            }

            if (!orderFound) {
                setValues({ askForVerification: false, orderDetails: null });
            } else {
                const failedDelivery = orderDetails.currentStatus === 'FAILED_DELIVERY';
                if (failedDelivery) {
                    setValues({ askForVerification: true, orderDetails: orderDetails });
                } else {
                    setValues({ askForVerification: false, orderDetails: orderDetails });
                }
            }

            setData(orderDetails || []);
            setFound(orderFound);

        }).catch((err) => {
            console.error(err);
        }).finally(() => {
            setIsLoading(false);
        });
    }

    React.useEffect(() => {
        const request = {}
        if ((searchBy && value) || id || awbSearch) {
            setIsLoading(true);
            if (searchBy === 'reference') {
                request.reference = value;
            } else if (searchBy === 'waybill') {
                request.waybills = value;
            } else if (id) {
                request.waybills = id;
            } else if (awbSearch) {
                request.waybills = awbSearch;
            }

            if (merchantId && !SKIP_MERCHANT_SCOPE) {
                request.merchantId = merchantId;
            }

            fetchData(request);
        }
        // eslint-disable-next-line
    }, [searchBy, value, id, awbSearch, merchantId]);

    // "Track another order" navigates to "/" without remounting this component (both routes render
    // the same element), so the loaded order would otherwise survive in local state. When the URL
    // carries no lookup anymore, reset to the fresh search state.
    React.useEffect(() => {
        if (!((searchBy && value) || id || awbSearch)) {
            setData([]);
            setFound(true);
        }
    }, [searchBy, value, id, awbSearch]);

    // The template owns the constant chrome (header/footer) on EVERY state; we feed the right
    // center — the search box while nothing is loaded (with the not-found alert beneath it after a
    // failed lookup, so the user can retry — same as the legacy page), else null (the template
    // renders its own tracking body from `order`).
    let center = null;
    if (!data?.createdDate) {
        center = (
            <>
                <SearchBar />
                {(!isLoading && !found) && <Box sx={{ mt: 2 }}><OrderNotFoundScreen /></Box>}
            </>
        );
    }

    const designTemplate = (brand?.designTemplate || 'CLASSIC').toUpperCase();
    return (
        <TemplateRenderer template={designTemplate} brand={brand} order={data?.createdDate ? data : null}>
            {center}
        </TemplateRenderer>
    );
}
